/**
 * Input Validation Utilities
 * Addresses Peter's review feedback on input bounds validation and security
 */

export interface ValidationRules {
  min?: number;
  max?: number;
  allowInfinity?: boolean;
  allowNegative?: boolean;
  maxDecimalPlaces?: number;
}

export interface ValidationResult {
  isValid: boolean;
  value: number;
  error?: string;
}

/**
 * Safely parse and validate numeric input with bounds checking
 * Prevents Infinity, -Infinity, NaN, and out-of-bounds values
 */
export const safeParseFloat = (
  input: string | number, 
  rules: ValidationRules = {},
  fallback: number = 0
): ValidationResult => {
  const {
    min = -Number.MAX_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    allowInfinity = false,
    allowNegative = true,
    maxDecimalPlaces = 10
  } = rules;

  // Handle number input
  if (typeof input === 'number') {
    input = input.toString();
  }

  // Clean the input string
  const cleanValue = input.replace(/[^-0-9.]/g, '');
  
  if (!cleanValue || cleanValue === '-') {
    return { isValid: true, value: fallback };
  }

  const parsed = parseFloat(cleanValue);

  // Check for invalid numbers
  if (isNaN(parsed)) {
    return {
      isValid: false,
      value: fallback,
      error: 'Invalid number format'
    };
  }

  // Check for infinity
  if (!isFinite(parsed) && !allowInfinity) {
    return {
      isValid: false,
      value: fallback,
      error: 'Value cannot be infinite'
    };
  }

  // Check negative values
  if (parsed < 0 && !allowNegative) {
    return {
      isValid: false,
      value: fallback,
      error: 'Value cannot be negative'
    };
  }

  // Check bounds
  if (parsed < min) {
    return {
      isValid: false,
      value: min,
      error: `Value cannot be less than ${min}`
    };
  }

  if (parsed > max) {
    return {
      isValid: false,
      value: max,
      error: `Value cannot be greater than ${max}`
    };
  }

  // Check decimal places
  const decimalPlaces = (cleanValue.split('.')[1] || '').length;
  if (decimalPlaces > maxDecimalPlaces) {
    return {
      isValid: false,
      value: parseFloat(parsed.toFixed(maxDecimalPlaces)),
      error: `Too many decimal places (max ${maxDecimalPlaces})`
    };
  }

  return { isValid: true, value: parsed };
};

/**
 * Common validation rules for different input types
 */
export const VALIDATION_RULES = {
  // Financial amounts
  CURRENCY: {
    min: 0,
    max: 100000000, // 100 million
    allowNegative: false,
    maxDecimalPlaces: 2
  } as ValidationRules,

  // Percentages (0-100)
  PERCENTAGE: {
    min: 0,
    max: 100,
    allowNegative: false,
    maxDecimalPlaces: 3
  } as ValidationRules,

  // Interest rates (0-50%)
  INTEREST_RATE: {
    min: 0,
    max: 50,
    allowNegative: false,
    maxDecimalPlaces: 3
  } as ValidationRules,

  // Years
  YEARS: {
    min: 0,
    max: 100,
    allowNegative: false,
    maxDecimalPlaces: 1
  } as ValidationRules,

  // General positive numbers
  POSITIVE_NUMBER: {
    min: 0,
    allowNegative: false,
    maxDecimalPlaces: 4
  } as ValidationRules,

  // Any number (can be negative)
  ANY_NUMBER: {
    allowNegative: true,
    maxDecimalPlaces: 4
  } as ValidationRules
};

/**
 * Validate localStorage data before use
 */
export const validateStoredData = <T>(
  data: any,
  validator: (data: any) => data is T
): T | null => {
  try {
    if (validator(data)) {
      return data;
    }
    console.log('Invalid stored data format, ignoring');
    return null;
  } catch (error) {
    console.error('Error validating stored data:', error);
    return null;
  }
};

/**
 * Sanitize data before storing to localStorage
 */
export const sanitizeForStorage = (data: any): string => {
  try {
    // Remove any potentially dangerous properties
    const sanitized = JSON.parse(JSON.stringify(data, (key, value) => {
      // Skip functions and undefined values
      if (typeof value === 'function' || value === undefined) {
        return undefined;
      }
      
      // Validate numeric values
      if (typeof value === 'number' && !isFinite(value)) {
        return 0;
      }
      
      return value;
    }));
    
    return JSON.stringify(sanitized);
  } catch (error) {
    console.error('Error sanitizing data for storage:', error);
    throw new Error('Cannot serialize data for storage');
  }
};